import {
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AnimatedNumber from "../../common/components/AnimatedNumber";
import Button from "../../common/components/Button";
import DateRangeSelector from "../../common/components/DateRangeSelector";
import PageHeader from "../../common/components/PageHeader";
import { toast } from "../../common/components/Toast";
import { getProducerDetail } from "../api/financials";

function ProducerDetail() {
  const { t } = useTranslation("financials");
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const periodStart = params.get("period_start");
    const periodEnd = params.get("period_end");

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getProducerDetail(userId, {
          period_start: periodStart,
          period_end: periodEnd,
        });
        setData(result);
      } catch (error) {
        toast.error(error.message || "Failed to load producer details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search, userId]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Spinner animation="border" />
      </Box>
    );
  }

  if (!data) return null;

  const { producer, earnings_summary, asset_balances, payout_history } = data;

  // Derive active period from URL
  const params = new URLSearchParams(location.search);
  const periodStart = params.get("period_start");
  const periodEnd = params.get("period_end");
  const periodLabel =
    periodStart && periodEnd
      ? `${new Date(periodStart).toLocaleDateString()} — ${new Date(periodEnd).toLocaleDateString()}`
      : "All Time";

  return (
    <>
      <PageHeader
        title={producer.user_name}
        description={producer.email}
        appendActions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              onClick={() => navigate("/financials")}
            >
              {t("producerDetail.backToFinancials")}
            </Button>
            <DateRangeSelector />
          </Box>
        }
      />

      {/* Profile Card */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Chip label={producer.role} color="primary" variant="outlined" />
            {producer.revenue_share_percentage && (
              <Typography variant="body2" color="text.secondary">
                {t("producerDetail.revenueShare")}:{" "}
                {producer.revenue_share_percentage}%
              </Typography>
            )}
            {producer.parent_name && (
              <Typography variant="body2" color="text.secondary">
                {t("producerDetail.parentProducer")}: {producer.parent_name}
              </Typography>
            )}
            <Chip
              label={periodLabel}
              size="small"
              variant="outlined"
              sx={{ ml: "auto" }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Earnings Summary */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              textTransform: "uppercase",
              fontWeight: 500,
              fontSize: "0.875rem",
              color: "text.secondary",
              mb: 2,
            }}
          >
            {t("producerDetail.summaryTitle")}
          </Typography>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t("producerDetail.totalEarned")}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                <AnimatedNumber
                  section="statsCards"
                  value={earnings_summary.total_earnings}
                  isCurrency
                />
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t("producerDetail.totalPaid")}
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "success.main" }}
              >
                <AnimatedNumber
                  section="statsCards"
                  value={earnings_summary.total_paid}
                  isCurrency
                />
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t("producerDetail.remaining")}
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: "warning.main" }}
              >
                <AnimatedNumber
                  section="statsCards"
                  value={earnings_summary.remaining}
                  isCurrency
                />
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Asset Breakdown */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              textTransform: "uppercase",
              fontWeight: 500,
              fontSize: "0.875rem",
              color: "text.secondary",
              mb: 2,
            }}
          >
            {t("producerDetail.assetBreakdown")}
          </Typography>
          {asset_balances.length === 0 ? (
            <Typography color="text.secondary">
              {t("producerDetail.noAssets")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("producerDetail.columns.title")}</TableCell>
                  <TableCell align="right">
                    {t("producerDetail.totalEarned")}
                  </TableCell>
                  <TableCell align="right">
                    {t("producerDetail.totalPaid")}
                  </TableCell>
                  <TableCell align="right">
                    {t("producerDetail.remaining")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asset_balances.map((a) => (
                  <TableRow key={a.asset_id}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {a.asset_title}
                    </TableCell>
                    <TableCell align="right">
                      ${a.total_earnings?.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      ${a.total_paid?.toFixed(2)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color:
                          a.remaining > 0 ? "warning.main" : "success.main",
                      }}
                    >
                      ${a.remaining?.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payout History */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              textTransform: "uppercase",
              fontWeight: 500,
              fontSize: "0.875rem",
              color: "text.secondary",
              mb: 2,
            }}
          >
            {t("producerDetail.payoutHistory")}
          </Typography>
          {payout_history.length === 0 ? (
            <Typography color="text.secondary">
              {t("producerDetail.noPayouts")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("producerDetail.columns.date")}</TableCell>
                  <TableCell align="right">
                    {t("producerDetail.columns.amount")}
                  </TableCell>
                  <TableCell>{t("producerDetail.columns.asset")}</TableCell>
                  <TableCell>{t("producerDetail.columns.notes")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payout_history.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{formatDate(p.created_at)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      ${parseFloat(p.amount).toFixed(2)}
                    </TableCell>
                    <TableCell>{p.asset_title || "-"}</TableCell>
                    <TableCell>
                      <Typography
                        variant="bodySm"
                        color="text.secondary"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.notes || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export default ProducerDetail;
